import {
  CHAT_CHANNEL_MEMBER_TYPE,
  EDGE_TYPE,
  INGREDIENT_TYPE,
  ORGANIZATION_SEARCH_PREFERENCE_TYPE,
  PROPERTY_TYPE,
  RECIPE_INGREDIENT_TYPE
} from '@ent/entity-type';
import { parseMeasure } from '@utils/measure-utils';
import { stableId, stableSequences } from './stable-values';
import type {
  AddAgentArgs,
  AddChatChannelMembersArgs,
  AddRecipeIngredientArgs,
  AddRecipeToMenuArgs,
  ArchiveKnowledgeArticleArgs,
  CreateAnnouncementArgs,
  ClearChatChannelArgs,
  CreateChatChannelArgs,
  CreateChatMessageArgs,
  CreateKnowledgeArticleArgs,
  DeleteChatMessageArgs,
  CreateFileArgs,
  CreateInventoryItemArgs,
  CreateLogbookEntryArgs,
  CreateMenuArgs,
  CreateRecipeArgs,
  CreateUnavailableItemArgs,
  DeleteMenuArgs,
  InviteEmployeeArgs,
  MarkAnnouncementReadArgs,
  MarkChatChannelReadArgs,
  PublishKnowledgeArticleArgs,
  RemoveAgentArgs,
  RemoveChatChannelMemberArgs,
  ResetSearchPreferenceDefaultArgs,
  ResolveUnavailableItemArgs,
  RemoveFileArgs,
  SetDefaultOrganizationArgs,
  SetMeasurementSystemArgs,
  SetSearchPreferenceDefaultArgs,
  UpdateChatChannelArgs,
  UpdateChatMessageArgs,
  UpdateEmployeeArgs,
  UpdateInventoryItemArgs,
  UpdateInventoryQuantityArgs,
  UpdateKnowledgeArticleArgs,
  UpdateMenuArgs,
  UpdateRecipeArgs,
  UpdateRecipeIngredientArgs,
  UpdateUnavailableItemArgs
} from './mutator-types';

function mutationNow(args: { id?: string; now?: number | undefined }): number {
  return args.now ?? 0;
}

async function insertOptimisticFile(
  tx: any,
  args: CreateFileArgs,
  globalSequence: number
) {
  const now = mutationNow(args);
  await tx.mutate.files.insert({
    id: args.id,
    organizationId: args.organizationId,
    parentId: args.parentId,
    parentType: args.parentType,
    contentType: args.contentType,
    sizeBytes: args.sizeBytes,
    filename: args.filename,
    width: args.width ?? null,
    height: args.height ?? null,
    uploadedByUserId: args.uploadedByUserId ?? '',
    createdAt: now,
    updatedAt: now,
    globalSequence
  });
}

/**
 * Client-side OPTIMISTIC implementations: write directly to the local
 * replica via tx.mutate so the UI updates immediately, ahead of the
 * server's authoritative round-trip (see mutators.ts). Plain functions --
 * not defineMutator() -- because the registry form breaks
 * z.mutate.<table>.<name>(args) client-side dispatch in this Zero version
 * ("z.mutate.chatMessages.create is not a function" at runtime).
 *
 * IDs and timestamps are derived from call-site args so Zero rebases
 * replay to the same local row set. `tx.mutate.*` here is the custom
 * mutator's internal write API, not the public legacy CRUD surface.
 */
export const clientMutators = {
  organizationUsers: {
    invite: async (tx: any, args: InviteEmployeeArgs) => {
      // Optimistic-only: always renders as a brand-new user+membership pair
      // so the employee list updates instantly. The server mutator invites
      // by email and may instead link an existing user -- if so, replication
      // reconciles away this speculative row (same tradeoff documented on
      // InviteEmployeeArgs.userId).
      await tx.mutate.users.insert({
        id: args.userId,
        clerkId: `TEMP_CLERK_ID_${args.userId}`,
        firstName: args.firstName ?? null,
        lastName: args.lastName ?? null,
        primaryEmailAddress: args.primaryEmailAddress,
        primaryPhoneNumber: null
      });
      const [orgUserSeq] = stableSequences(args.id, 1);
      await tx.mutate.organizationUsers.insert({
        id: args.id,
        organizationId: args.organizationId,
        userId: args.userId,
        role: args.role,
        position: args.position ?? null,
        globalSequence: orgUserSeq
      });
    },
    update: async (tx: any, args: UpdateEmployeeArgs) => {
      await tx.mutate.users.update({
        id: args.userId,
        firstName: args.firstName,
        lastName: args.lastName,
        primaryEmailAddress: args.primaryEmailAddress
      });
      if (args.position !== undefined) {
        for (const id of args.organizationUserIds ?? []) {
          await tx.mutate.organizationUsers.update({
            id,
            position: args.position
          });
        }
      }
    }
  },
  announcements: {
    create: async (tx: any, args: CreateAnnouncementArgs) => {
      const now = mutationNow(args);
      const [announcementSeq, edgeSeq] = stableSequences(args.id, 2);
      await tx.mutate.announcements.insert({
        id: args.id,
        title: args.title,
        body: args.body,
        pinned: args.pinned ?? false,
        publishAt: args.publishAt ?? null,
        expiresAt: args.expiresAt ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: announcementSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'org'),
        sourceId: args.organizationId,
        targetId: args.id,
        globalSequence: edgeSeq
      });
    },
    // Optimistic-only no-op: the client doesn't have the current user's id
    // in scope at the call site to target a speculative read-edge row (same
    // tradeoff as users.setDefaultOrganization below). The server mutator
    // creates the read edge authoritatively; replication reconciles the
    // "seen by" count in shortly after.
    markRead: async (_tx: any, _args: MarkAnnouncementReadArgs) => {}
  },
  chatChannels: {
    create: async (tx: any, args: CreateChatChannelArgs) => {
      const now = mutationNow(args);
      const invitees = (args.memberUserIds ?? []).filter(
        id => id !== args.creatorUserId
      );
      const isPrivate = !args.dmChannel && Boolean(args.isPrivate);
      const memberIds =
        args.creatorUserId && (args.dmChannel || isPrivate)
          ? [...new Set([args.creatorUserId, ...invitees])].sort()
          : [];
      const [channelSeq, edgeSeq, ...memberSequences] = stableSequences(
        args.id,
        2 + memberIds.length
      );
      await tx.mutate.chatChannels.insert({
        id: args.id,
        name: args.name,
        description: args.description ?? null,
        topic: args.topic ?? null,
        dmChannel: args.dmChannel,
        isPrivate,
        organizationId: args.organizationId,
        dmPairKey: args.dmChannel ? memberIds.join(':') : null,
        createdByUserId: args.creatorUserId ?? null,
        parentId: null,
        createdAt: now,
        updatedAt: now,
        globalSequence: channelSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'org'),
        sourceId: args.id,
        targetId: args.organizationId,
        globalSequence: edgeSeq
      });
      for (const [index, userId] of memberIds.entries()) {
        await tx.mutate.chatChannelMembers.insert({
          id: stableId(CHAT_CHANNEL_MEMBER_TYPE, args.id, userId),
          organizationId: args.organizationId,
          channelId: args.id,
          userId,
          createdAt: now,
          updatedAt: now,
          globalSequence: memberSequences[index]
        });
      }
    },
    update: async (tx: any, args: UpdateChatChannelArgs) => {
      const now = mutationNow(args);
      const patch: Record<string, unknown> = { id: args.id, updatedAt: now };
      if (args.name !== undefined) patch.name = args.name;
      if (args.topic !== undefined) patch.topic = args.topic;
      if (args.description !== undefined) patch.description = args.description;
      await tx.mutate.chatChannels.update(patch);
    },
    addMembers: async (tx: any, args: AddChatChannelMembersArgs) => {
      const now = mutationNow({ id: args.channelId, now: args.now });
      const userIds = [...new Set(args.userIds)];
      const sequences = stableSequences(
        `${args.channelId}:members:${now}`,
        userIds.length
      );
      for (const [index, userId] of userIds.entries()) {
        await tx.mutate.chatChannelMembers.upsert({
          id: stableId(CHAT_CHANNEL_MEMBER_TYPE, args.channelId, userId),
          organizationId: args.organizationId,
          channelId: args.channelId,
          userId,
          createdAt: now,
          updatedAt: now,
          globalSequence: sequences[index]
        });
      }
    },
    removeMember: async (tx: any, args: RemoveChatChannelMemberArgs) => {
      await tx.mutate.chatChannelMembers.delete({
        id:
          args.memberId ??
          stableId(CHAT_CHANNEL_MEMBER_TYPE, args.channelId, args.userId)
      });
    },
    // Agent-membership fields (agent_enabled/agent_paused/added_by_user_id/
    // history_start_boundary/response_mode) are deliberately excluded from
    // the Zero replication allowlist for now (drizzle-zero.config.ts), the
    // same way chat_messages.protected_body is -- there is nothing yet to
    // optimistically render for them. addAgent still materializes the
    // member row itself (same shape as addMembers) so the new participant
    // shows up instantly; the agent-only columns arrive via replication.
    addAgent: async (tx: any, args: AddAgentArgs) => {
      const now = mutationNow({ id: args.channelId, now: args.now });
      const [sequence] = stableSequences(
        `${args.channelId}:agent:${args.agentUserId}:${now}`,
        1
      );
      await tx.mutate.chatChannelMembers.upsert({
        id: stableId(
          CHAT_CHANNEL_MEMBER_TYPE,
          args.channelId,
          args.agentUserId
        ),
        organizationId: args.organizationId,
        channelId: args.channelId,
        userId: args.agentUserId,
        createdAt: now,
        updatedAt: now,
        globalSequence: sequence
      });
    },
    removeAgent: async (tx: any, args: RemoveAgentArgs) => {
      await tx.mutate.chatChannelMembers.delete({
        id: stableId(CHAT_CHANNEL_MEMBER_TYPE, args.channelId, args.agentUserId)
      });
    },
    // No replicated columns to update optimistically -- see the addAgent
    // comment above. The server mutation is authoritative; the client
    // simply waits for replication.
    setAgentPaused: async () => {},
    setAgentResponseMode: async () => {},
    shareAgentHistory: async () => {}
  },
  chatMessages: {
    create: async (tx: any, args: CreateChatMessageArgs) => {
      const now = mutationNow(args);
      const [messageSeq, edgeSeq, ...fileSequences] = stableSequences(
        args.id,
        2 + (args.attachments?.length ?? 0)
      );
      await tx.mutate.chatMessages.insert({
        id: args.id,
        text: args.text,
        senderId: args.senderId,
        parentId: null,
        createdAt: now,
        updatedAt: now,
        globalSequence: messageSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'channel'),
        sourceId: args.id,
        targetId: args.channelId,
        globalSequence: edgeSeq
      });
      for (const [index, file] of (args.attachments ?? []).entries()) {
        await insertOptimisticFile(
          tx,
          {
            ...file,
            organizationId: args.organizationId ?? '',
            parentId: args.id,
            parentType: 'chat_messages',
            uploadedByUserId: args.senderId,
            now
          },
          fileSequences[index]
        );
      }
    },
    update: async (tx: any, args: UpdateChatMessageArgs) => {
      const now = mutationNow(args);
      await tx.mutate.chatMessages.update({
        id: args.id,
        text: args.text.trim(),
        updatedAt: now
      });
    },
    delete: async (tx: any, args: DeleteChatMessageArgs) => {
      for (const fileId of args.fileIds ?? []) {
        await tx.mutate.files.delete({ id: fileId });
      }
      await tx.mutate.edges.delete({
        id: stableId(EDGE_TYPE, args.id, 'channel')
      });
      await tx.mutate.chatMessages.delete({ id: args.id });
    },
    // Best-effort optimistic mirror of the server's clearChannel: the
    // server is authoritative on which messages actually get deleted (and
    // on the DM-vs-manager authorization check), this just makes the local
    // replica catch up immediately instead of waiting for the round trip.
    clearChannel: async (tx: any, args: ClearChatChannelArgs) => {
      const channel = await tx.query.chatChannels
        .where('id', args.channelId)
        .related('chatMessages')
        .one();
      for (const message of channel?.chatMessages ?? []) {
        await tx.mutate.edges.delete({
          id: stableId(EDGE_TYPE, message.id, 'channel')
        });
        await tx.mutate.chatMessages.delete({ id: message.id });
      }
    }
  },
  files: {
    create: async (tx: any, args: CreateFileArgs) => {
      const [sequence] = stableSequences(args.id, 1);
      await insertOptimisticFile(tx, args, sequence);
    },
    remove: async (tx: any, args: RemoveFileArgs) => {
      await tx.mutate.files.delete({ id: args.id });
    }
  },
  chatChannelReads: {
    mark: async (tx: any, args: MarkChatChannelReadArgs) => {
      const now = mutationNow(args);
      const [sequence] = stableSequences(args.id, 1);
      await tx.mutate.chatChannelReads.upsert({
        id: args.id,
        organizationId: args.organizationId,
        channelId: args.channelId,
        userId: args.userId,
        lastReadAt: now,
        lastReadMessageId: args.lastReadMessageId ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: sequence
      });
    }
  },
  logbookEntries: {
    create: async (tx: any, args: CreateLogbookEntryArgs) => {
      const now = mutationNow(args);
      const [entrySeq, edgeSeq] = stableSequences(args.id, 2);
      await tx.mutate.managerLogbookEntries.insert({
        id: args.id,
        title: args.title,
        body: args.body,
        authorId: args.authorId,
        shiftDate: args.shiftDate,
        shiftType: args.shiftType ?? null,
        tags: args.tags ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: entrySeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'org'),
        sourceId: args.id,
        targetId: args.organizationId,
        globalSequence: edgeSeq
      });
    }
  },
  inventoryItems: {
    create: async (tx: any, args: CreateInventoryItemArgs) => {
      const now = mutationNow(args);
      const [itemSeq, edgeSeq, propSeq, propEdgeSeq, catEdgeSeq] =
        stableSequences(args.id, 5);
      await tx.mutate.inventoryItems.insert({
        id: args.id,
        name: args.name,
        key: args.key,
        notes: args.notes ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: itemSeq
      });
      const orgEdgeId = stableId(EDGE_TYPE, args.id, 'org');
      await tx.mutate.edges.insert({
        id: orgEdgeId,
        sourceId: args.organizationId,
        targetId: args.id,
        globalSequence: edgeSeq
      });
      const propertyId = stableId(PROPERTY_TYPE, args.id, 'quantity');
      await tx.mutate.properties.insert({
        id: propertyId,
        key: 'quantity',
        valueType: 'number',
        numberValue: args.quantity ?? 1,
        stringValue: null,
        createdAt: now,
        updatedAt: now,
        globalSequence: propSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'quantity'),
        sourceId: orgEdgeId,
        targetId: propertyId,
        globalSequence: propEdgeSeq
      });
      if (args.categoryId) {
        await tx.mutate.edges.insert({
          id: stableId(EDGE_TYPE, args.id, 'category', args.categoryId),
          sourceId: args.id,
          targetId: args.categoryId,
          globalSequence: catEdgeSeq
        });
      }
    },
    update: async (tx: any, args: UpdateInventoryItemArgs) => {
      const now = mutationNow(args);
      await tx.mutate.inventoryItems.update({
        id: args.id,
        name: args.name,
        notes: args.notes ?? null,
        updatedAt: now
      });
      if (args.categoryId) {
        const [catEdgeSeq] = stableSequences(`${args.id}:category`, 1);
        await tx.mutate.edges.insert({
          id: stableId(EDGE_TYPE, args.id, 'category', args.categoryId),
          sourceId: args.id,
          targetId: args.categoryId,
          globalSequence: catEdgeSeq
        });
      }
    },
    updateQuantity: async (tx: any, args: UpdateInventoryQuantityArgs) => {
      const now = mutationNow(args);
      if (args.quantity < 0) {
        if (args.quantityPropertyId) {
          await tx.mutate.properties.delete({ id: args.quantityPropertyId });
        }
        if (args.orgItemEdgeId) {
          await tx.mutate.edges.delete({ id: args.orgItemEdgeId });
        }
        return;
      }
      if (args.orgItemEdgeId && args.quantityPropertyId) {
        await tx.mutate.properties.update({
          id: args.quantityPropertyId,
          numberValue: args.quantity,
          updatedAt: now
        });
        return;
      }
      const seed = `${args.organizationId}:${args.itemId}`;
      const [edgeSeq, propSeq, propEdgeSeq] = stableSequences(seed, 3);
      const edgeId = stableId(EDGE_TYPE, seed, 'org-item');
      const propertyId = stableId(PROPERTY_TYPE, seed, 'quantity');
      await tx.mutate.edges.insert({
        id: edgeId,
        sourceId: args.organizationId,
        targetId: args.itemId,
        globalSequence: edgeSeq
      });
      await tx.mutate.properties.insert({
        id: propertyId,
        key: 'quantity',
        valueType: 'number',
        numberValue: args.quantity,
        createdAt: now,
        updatedAt: now,
        stringValue: null,
        globalSequence: propSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, seed, 'quantity'),
        sourceId: edgeId,
        targetId: propertyId,
        globalSequence: propEdgeSeq
      });
    }
  },
  recipes: {
    create: async (tx: any, args: CreateRecipeArgs) => {
      const now = mutationNow(args);
      const [recipeSeq, edgeSeq] = stableSequences(args.id, 2);
      await tx.mutate.recipes.insert({
        id: args.id,
        name: args.name,
        subtitle: args.subtitle,
        instructions: args.instructions,
        notes: args.notes ?? null,
        status: args.status ?? 'draft',
        preparation: args.preparation ?? null,
        serving: args.serving ?? null,
        service: args.service ?? null,
        unitSystem: args.unitSystem ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: recipeSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'org'),
        sourceId: args.organizationId,
        targetId: args.id,
        globalSequence: edgeSeq
      });
    },
    update: async (tx: any, args: UpdateRecipeArgs) => {
      const now = mutationNow(args);
      await tx.mutate.recipes.update({
        id: args.id,
        name: args.name,
        subtitle: args.subtitle,
        instructions: args.instructions,
        notes: args.notes ?? null,
        ...(args.status ? { status: args.status } : {}),
        preparation: args.preparation ?? null,
        serving: args.serving ?? null,
        service: args.service ?? null,
        unitSystem: args.unitSystem ?? null,
        updatedAt: now
      });
    },
    addIngredient: async (tx: any, args: AddRecipeIngredientArgs) => {
      const now = mutationNow(args);
      const seed = `${args.recipeId}:${args.fullName}:${args.quantityRaw}`;
      const [ingredientSeq, riSeq] = stableSequences(seed, 2);
      const ingredientId = stableId(INGREDIENT_TYPE, seed);
      await tx.mutate.ingredients.insert({
        id: ingredientId,
        fullName: args.fullName,
        brandName: null,
        description: null,
        notes: null,
        createdAt: now,
        updatedAt: now,
        globalSequence: ingredientSeq
      });
      // Must produce byte-identical values to the server mutator in
      // mutators.ts, or the optimistic row flickers when the server row
      // arrives.
      const measure = parseMeasure(args.quantityRaw);
      await tx.mutate.recipeIngredients.insert({
        id: stableId(RECIPE_INGREDIENT_TYPE, seed),
        sourceId: args.recipeId,
        targetId: ingredientId,
        quantity: measure.quantity,
        quantityStr: measure.quantityStr,
        quantityType: measure.quantityType,
        createdAt: now,
        updatedAt: now,
        globalSequence: riSeq
      });
    }
  },
  recipeIngredients: {
    update: async (tx: any, args: UpdateRecipeIngredientArgs) => {
      const now = mutationNow(args);
      const measure = parseMeasure(args.quantityRaw);
      await tx.mutate.recipeIngredients.update({
        id: args.id,
        quantity: measure.quantity,
        quantityStr: measure.quantityStr,
        quantityType: measure.quantityType,
        updatedAt: now
      });
    }
  },
  menus: {
    create: async (tx: any, args: CreateMenuArgs) => {
      const now = mutationNow(args);
      const [menuSeq, edgeSeq] = stableSequences(args.id, 2);
      await tx.mutate.menus.insert({
        id: args.id,
        name: args.name,
        subtitle: args.subtitle,
        notes: args.notes,
        status: args.status,
        kind: args.kind ?? 'beverage',
        description: args.description ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: menuSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'org'),
        sourceId: args.organizationId,
        targetId: args.id,
        globalSequence: edgeSeq
      });
    },
    update: async (tx: any, args: UpdateMenuArgs) => {
      const now = mutationNow(args);
      await tx.mutate.menus.update({
        id: args.id,
        name: args.name,
        subtitle: args.subtitle,
        notes: args.notes,
        status: args.status,
        ...(args.kind ? { kind: args.kind } : {}),
        description: args.description ?? null,
        updatedAt: now
      });
    },
    addRecipe: async (tx: any, args: AddRecipeToMenuArgs) => {
      const [edgeSeq] = stableSequences(`${args.menuId}:${args.recipeId}`, 1);
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.menuId, 'recipe', args.recipeId),
        sourceId: args.menuId,
        targetId: args.recipeId,
        globalSequence: edgeSeq
      });
    },
    delete: async (tx: any, args: DeleteMenuArgs) => {
      await tx.mutate.menus.delete({ id: args.id });
    }
  },
  unavailableItems: {
    create: async (tx: any, args: CreateUnavailableItemArgs) => {
      const now = mutationNow(args);
      const [itemSeq, edgeSeq] = stableSequences(args.id, 2);
      await tx.mutate.unavailableItems.insert({
        id: args.id,
        name: args.name,
        department: args.department,
        reason: args.reason ?? null,
        expectedBack: args.expectedBack ?? null,
        resolvedAt: null,
        linkedInventoryItemId: args.linkedInventoryItemId ?? null,
        linkedWareId: args.linkedWareId ?? null,
        linkedIngredientId: args.linkedIngredientId ?? null,
        createdAt: now,
        updatedAt: now,
        globalSequence: itemSeq
      });
      await tx.mutate.edges.insert({
        id: stableId(EDGE_TYPE, args.id, 'org'),
        sourceId: args.organizationId,
        targetId: args.id,
        globalSequence: edgeSeq
      });
    },
    update: async (tx: any, args: UpdateUnavailableItemArgs) => {
      const now = mutationNow(args);
      await tx.mutate.unavailableItems.update({
        id: args.id,
        name: args.name,
        department: args.department,
        reason: args.reason ?? null,
        expectedBack: args.expectedBack ?? null,
        linkedWareId: args.linkedWareId ?? null,
        linkedIngredientId: args.linkedIngredientId ?? null,
        updatedAt: now
      });
    },
    resolve: async (tx: any, args: ResolveUnavailableItemArgs) => {
      const now = mutationNow(args);
      await tx.mutate.unavailableItems.update({
        id: args.id,
        resolvedAt: now,
        updatedAt: now
      });
    }
  },
  users: {
    // No-op: users.defaultOrganizationId is now exposed to Zero (see
    // drizzle-zero.config.ts), but this mutator's only caller
    // (org-switcher.tsx) doesn't have the current user's id in scope to
    // target the optimistic patch at -- left as a server round-trip rather
    // than threading that through for a call site outside this change.
    setDefaultOrganization: async (
      _tx: any,
      _args: SetDefaultOrganizationArgs
    ) => {},
    // Same reasoning as setDefaultOrganization above: a plain server
    // round-trip rather than an optimistic local patch. This setting is
    // rarely changed and every read of it already goes through Zero's
    // reactive `queries.users.current()` query, so the confirmed value
    // simply arrives moments later with no visible flash of stale content.
    setMeasurementSystem: async (
      _tx: any,
      _args: SetMeasurementSystemArgs
    ) => {}
  },
  searchPreferences: {
    // `args.userId` is a client-supplied hint used only to target the same
    // deterministic row id the server converges on (EntOrganizationSearchPreference.
    // preferenceId uses the identical stableId(type, orgId, userId) formula);
    // the server always derives real ownership from its own auth context.
    // Without a userId (caller didn't pass one), this optimistically
    // no-ops and the confirmed save arrives with the server round-trip.
    setDefault: async (tx: any, args: SetSearchPreferenceDefaultArgs) => {
      if (!args.userId) return;
      const now = mutationNow(args);
      const id = stableId(
        ORGANIZATION_SEARCH_PREFERENCE_TYPE,
        args.organizationId,
        args.userId
      );
      const existing = await tx.query.organizationSearchPreferences
        .where('id', id)
        .one();
      const contentTypeFilters =
        args.contentTypeFilters === undefined
          ? (existing?.contentTypeFilters ?? null)
          : args.contentTypeFilters === null
            ? null
            : JSON.stringify(args.contentTypeFilters);
      if (existing) {
        await tx.mutate.organizationSearchPreferences.update({
          id,
          defaultQuery: args.defaultQuery,
          contentTypeFilters,
          preferenceVersion: (existing.preferenceVersion ?? 0) + 1,
          updatedAt: now
        });
      } else {
        await tx.mutate.organizationSearchPreferences.insert({
          id,
          organizationId: args.organizationId,
          userId: args.userId,
          defaultQuery: args.defaultQuery,
          contentTypeFilters,
          preferenceVersion: 1,
          createdAt: now,
          updatedAt: now,
          globalSequence: stableSequences(id, 1)[0]
        });
      }
    },
    // Optimistic-only no-op for the same reason as setDefault above when no
    // userId is threaded through; when it is, delete the known row id.
    resetDefault: async (tx: any, args: ResetSearchPreferenceDefaultArgs) => {
      if (!args.userId) return;
      const id = stableId(
        ORGANIZATION_SEARCH_PREFERENCE_TYPE,
        args.organizationId,
        args.userId
      );
      await tx.mutate.organizationSearchPreferences.delete({ id });
    }
  },
  knowledgeArticles: {
    create: async (tx: any, args: CreateKnowledgeArticleArgs) => {
      const now = mutationNow(args);
      await tx.mutate.knowledgeArticles.insert({
        id: args.id,
        organizationId: args.organizationId,
        kind: args.kind,
        title: args.title,
        subtitle: args.subtitle ?? null,
        status: 'draft',
        tags: args.tags?.length ? JSON.stringify(args.tags) : null,
        authorId: '',
        createdAt: now,
        updatedAt: now,
        globalSequence: stableSequences(args.id, 1)[0]
      });
    },
    update: async (tx: any, args: UpdateKnowledgeArticleArgs) => {
      const now = mutationNow(args);
      await tx.mutate.knowledgeArticles.update({
        id: args.id,
        ...(args.title !== undefined ? { title: args.title } : {}),
        ...(args.subtitle !== undefined ? { subtitle: args.subtitle } : {}),
        ...(args.tags !== undefined
          ? { tags: args.tags?.length ? JSON.stringify(args.tags) : null }
          : {}),
        updatedAt: now
      });
    },
    publish: async (tx: any, args: PublishKnowledgeArticleArgs) => {
      await tx.mutate.knowledgeArticles.update({
        id: args.id,
        status: 'published'
      });
    },
    archive: async (tx: any, args: ArchiveKnowledgeArticleArgs) => {
      await tx.mutate.knowledgeArticles.update({
        id: args.id,
        status: 'archived'
      });
    }
  }
};
